import {
  deleteAccount,
  deleteAccountData,
  downloadAccountExport,
  downloadTaxExport,
  fetchAccount,
  resendVerification,
  resetDemoData,
} from "../api/mobile";
import {
  bankFieldsHtml,
  businessAddressFieldsHtml,
  dueRuleFieldsHtml,
  gstFieldsHtml,
  wireDueRuleToggles,
} from "../components/forms";
import {
  abnLookupFieldHtml,
  confirmAbnBeforeSave,
  wireAbnLookup,
} from "../components/abnLookup";
import {
  logoEditorHtml,
  logoProfilePatch,
  readLogoStateFromRecord,
  refreshLogoEditorSection,
  wireLogoEditor,
  type LogoEditorState,
} from "../components/logoEditor";
import { panelHeaderHtml, formActionsHtml } from "../components/formActions";
import { showToast } from "../components/ui";
import { cache } from "../data/idb";
import { clearSession } from "../auth/session";
import {
  flushQueue,
  pullBootstrap,
  upsertBusiness,
  upsertSettings,
} from "../data/sync";
import { listAuFinancialYearOptions } from "../domain/taxExport";
import {
  formatAddressMultiline,
  normalizeAbn,
  normalizeAccountNumber,
  normalizeAuAddress,
  normalizeBsb,
  readAddressFromForm,
} from "../domain/address";
import { dueRuleFromFormData, dueRuleFromSettings } from "../domain/dueDates";
import { parseGstRegisteredForm, validateBusinessGstSettings } from "../domain/gst";
import { esc } from "../lib/escape";
import {
  applyDesktopUpdate,
  getDesktopUpdateStatus,
  isDesktopHost,
  type DesktopUpdateStatus,
} from "../lib/host";
import { wireFormScreen, wireHubBackButton } from "../lib/formActions";
import { attachFormGuard, attachCustomGuard, clearLeaveGuard, setGlobalSaveHandler } from "../lib/unsaved";
import { currentRouteTarget } from "../routes";
import { goAfterSave } from "../lib/navigation";
import { router } from "../router";
import type { AppContext } from "../types";

const SUPPORT_HUB_URL = "https://frogswork.com/support.html";
const SUPPORT_ISSUES_URL = "https://frogswork.com/issues.html";
const SUPPORT_GUIDES_URL = "https://frogswork.com/guides.html";
const SUPPORT_CONTACT_URL = "https://frogswork.com/contact.html";

export async function renderSettings(panel: HTMLElement, ctx: AppContext) {
  clearLeaveGuard();
  if (
    router.sub === "business" ||
    router.sub === "business-edit" ||
    router.sub === "business-add"
  ) {
    return renderBusiness(panel, ctx);
  }
  if (router.sub === "general") return renderGeneralHub(panel);
  if (router.sub === "logo") return renderLogoSettings(panel, ctx);
  if (router.sub === "payment-terms") return renderPaymentTerms(panel, ctx);
  if (router.sub === "email-sending") return renderEmailSending(panel, ctx);
  if (router.sub === "account" || router.sub === "sync") return renderAccount(panel, ctx);
  if (router.sub === "updates") return renderAppUpdate(panel);
  if (router.sub === "help") return renderHelp(panel);

  const desktop = isDesktopHost();
  let updateHint = "Check for a newer version";
  let updateHighlight = false;
  if (desktop) {
    const status = await getDesktopUpdateStatus(false);
    if (status?.apply_failed) {
      updateHint = `Update failed. Retry version ${status.apply_failed.version}`;
      updateHighlight = true;
    } else if (status?.pending) {
      updateHint = `Version ${status.pending.version} available`;
      updateHighlight = true;
    }
  }

  panel.innerHTML = `
    <h2 class="section-title">Settings</h2>
    <a class="nav-card" href="#settings/business">
      <span class="nav-card-title">Business details</span>
      <span class="nav-card-hint">Shown on your invoices</span>
    </a>
    <a class="nav-card" href="#settings/general">
      <span class="nav-card-title">General</span>
      <span class="nav-card-hint">Payment terms, logo, email, and quotes</span>
    </a>
    <a class="nav-card" href="#settings/account">
      <span class="nav-card-title">Account</span>
      <span class="nav-card-hint">Subscription, sync, and sign out</span>
    </a>
    ${
      desktop
        ? `<a class="nav-card${updateHighlight ? " nav-card-highlight" : ""}" href="#settings/updates">
      <span class="nav-card-title">App update</span>
      <span class="nav-card-hint">${esc(updateHint)}</span>
    </a>`
        : ""
    }
    <a class="nav-card" href="#settings/help">
      <span class="nav-card-title">Help &amp; support</span>
      <span class="nav-card-hint">Guides, troubleshooting, and contact</span>
    </a>`;
}

async function renderAppUpdate(panel: HTMLElement) {
  clearLeaveGuard();
  if (!isDesktopHost()) {
    router.navigate("settings");
    return;
  }

  const paint = (status: DesktopUpdateStatus | null, opts?: { checking?: boolean; error?: string }) => {
    const current = status?.current_version || "…";
    const pending = status?.pending;
    const failed = status?.apply_failed;
    const checking = Boolean(opts?.checking);
    const error = opts?.error || "";

    let body = "";
    if (!status) {
      body = `<p class="hint">Could not reach the desktop updater. Restart FrogsWork and try again.</p>`;
    } else if (!status.packaged) {
      body = `<p class="hint">Updates only run in the installed app. For development, rebuild with <code>client_app\\build.ps1</code>.</p>`;
    } else if (status.server_unreachable) {
      body = `<p class="error-text">Couldn't reach the server to check for updates. Try again when you're online.</p>`;
    } else if (pending || failed) {
      body = `
        ${
          failed
            ? `<p class="error-text">Last update to version ${esc(failed.version)} did not finish.${
                failed.error ? ` ${esc(failed.error)}.` : ""
              } Try again below, or download the installer from
              <a href="https://frogswork.com/download.html" target="_blank" rel="noopener">frogswork.com/download</a>.</p>`
            : ""
        }
        ${
          pending
            ? `<h3 class="settings-subhead" style="margin-top:0">Version ${esc(pending.version)} available</h3>
               <p class="hint">${esc(pending.notes || "A newer version of FrogsWork is ready to install.")}</p>
               <div class="btn-row">
                 <button type="button" class="btn" id="update-now">Update now</button>
               </div>`
            : `<h3 class="settings-subhead" style="margin-top:0">Retry update to version ${esc(
                failed?.version || ""
              )}</h3>
               <p class="hint">Check again if no update is listed, then choose Update now.</p>`
        }
        <p class="hint">The app downloads the update, restarts, and replaces its files. Your data in <code>%APPDATA%\\FrogsWork\\</code> is not touched.</p>`;
    } else {
      body = `<p class="success-text">You're on the latest version.</p>`;
    }

    panel.innerHTML = `
      <section class="panel">
        ${panelHeaderHtml({ title: "App update" })}
        <p class="hint">Installed version <strong>${esc(current)}</strong>. Updates don't change your invoices or account data.</p>
        ${error ? `<p class="error-text">${esc(error)}</p>` : ""}
        ${body}
        <div class="btn-row" style="margin-top:1rem">
          <button type="button" class="btn secondary" id="update-check" ${checking ? "disabled" : ""}>
            ${checking ? "Checking…" : "Check again"}
          </button>
        </div>
      </section>`;

    wireHubBackButton({ panel });
    panel.querySelector("#update-check")?.addEventListener("click", async () => {
      paint(status, { checking: true });
      const next = await getDesktopUpdateStatus(true);
      paint(next);
    });
    panel.querySelector("#update-now")?.addEventListener("click", async () => {
      const btn = panel.querySelector("#update-now") as HTMLButtonElement | null;
      if (btn) {
        btn.disabled = true;
        btn.textContent = "Downloading…";
      }
      const result = await applyDesktopUpdate();
      if (!result.ok) {
        showToast(result.error || "Update failed.", "error");
        const next = await getDesktopUpdateStatus(true);
        paint(next, { error: result.error });
        return;
      }
      panel.innerHTML = `
        <section class="panel">
          <h2>Updating…</h2>
          <p class="hint">Downloading version <strong>${esc(
            result.version || ""
          )}</strong>. FrogsWork will close and reopen when the update finishes.</p>
        </section>`;
    });
  };

  paint(null, { checking: true });
  const status = await getDesktopUpdateStatus(true);
  paint(status);
}

async function renderGeneralHub(panel: HTMLElement) {
  const settings = await cache.getSettings();
  const quotesEnabled = Boolean(settings.quotes_enabled);
  panel.innerHTML = `
    ${panelHeaderHtml({ title: "General" })}
    <a class="nav-card" href="#settings/payment-terms">
      <span class="nav-card-title">Payment terms</span>
      <span class="nav-card-hint">Default due date for new invoices</span>
    </a>
    <a class="nav-card" href="#settings/logo">
      <span class="nav-card-title">Logo</span>
      <span class="nav-card-hint">Invoice header logo placement</span>
    </a>
    <a class="nav-card" href="#settings/email-sending">
      <span class="nav-card-title">Email sending</span>
      <span class="nav-card-hint">CC, BCC, or no copy to you</span>
    </a>
    <section class="panel" style="margin-top:0.75rem">
      <h3 class="settings-subhead" style="margin-top:0">Quotes</h3>
      <p class="hint">Show a Quotes tab for quotes and price estimates. Off by default.</p>
      <label class="checkbox">
        <input type="checkbox" id="quotes-enabled" ${quotesEnabled ? "checked" : ""}>
        Enable quotes
      </label>
    </section>`;
  wireHubBackButton({ panel });
  panel.querySelector("#quotes-enabled")?.addEventListener("change", async (e) => {
    const checked = (e.target as HTMLInputElement).checked;
    try {
      await upsertSettings({ quotes_enabled: checked });
      const nav = document.getElementById("nav-quotes") as HTMLElement | null;
      if (nav) nav.hidden = !checked;
      showToast(checked ? "Quotes enabled." : "Quotes hidden.", "success");
    } catch (ex) {
      showToast(ex instanceof Error ? ex.message : "Could not save.", "error");
      (e.target as HTMLInputElement).checked = !checked;
    }
  });
}

async function renderLogoSettings(panel: HTMLElement, ctx: AppContext) {
  const businesses = await cache.getBusinesses();
  const settings = await cache.getSettings();
  const names = Object.keys(businesses).sort();
  if (!names.length) {
    panel.innerHTML = `
      ${panelHeaderHtml({ title: "Logo" })}
      <p class="hint">Add a business first, then set your invoice logo here.</p>
      <div class="btn-row">
        <button type="button" class="btn primary" id="goto-business">Add business</button>
      </div>`;
    panel.querySelector("#goto-business")?.addEventListener("click", () =>
      router.navigate("settings", "business-add")
    );
    wireHubBackButton({
      panel,
      onBack: () => router.navigate("settings", "general"),
    });
    return;
  }

  const defaultBiz = String(settings.default_business || "").trim();
  let selected = names.includes(defaultBiz) ? defaultBiz : names[0];
  let logoState = readLogoStateFromRecord(businesses[selected] || {});
  let guard: ReturnType<typeof attachCustomGuard> | null = null;
  let logoDirty = false;

  const paint = () => {
    const record = businesses[selected] || {};
    const multi = names.length > 1;
    panel.innerHTML = `
      <form id="logo-form" class="panel" novalidate>
        ${panelHeaderHtml({ title: "Logo" })}
        <p class="hint">Shown in the top-left of invoice and quote PDFs. Prefer a transparent PNG.</p>
        ${
          multi
            ? `<div class="field"><label>Business</label>
                <select id="logo-business">
                  ${names
                    .map(
                      (n) =>
                        `<option value="${esc(n)}" ${n === selected ? "selected" : ""}>${esc(
                          String(businesses[n]?.business_name || n)
                        )}</option>`
                    )
                    .join("")}
                </select></div>`
            : `<p class="hint">${esc(String(record.business_name || selected))}</p>`
        }
        ${logoEditorHtml(logoState)}
        <p class="error-text" id="form-error" hidden></p>
        ${formActionsHtml({ saveId: "save-btn" })}
      </form>`;

    guard = attachCustomGuard({
      isDirty: () => logoDirty,
      clear: () => {
        logoDirty = false;
      },
    });

    const saveHandler = async () => {
      const existing = businesses[selected] || {};
      const profile = logoProfilePatch(logoState, existing);
      await upsertBusiness(selected, profile);
      businesses[selected] = profile;
      logoDirty = false;
      void flushQueue(ctx.onSyncStatus).then((flush) => {
        if (!flush.ok) {
          showToast(flush.error || "Saved locally but sync failed. Will retry when online.", "error");
        }
      });
    };

    setGlobalSaveHandler(async () => {
      try {
        await saveHandler();
        guard?.clear();
        const cur = currentRouteTarget(router.tab, router.sub, router.params);
        goAfterSave(cur);
        return true;
      } catch {
        return false;
      }
    });

    const onLogoChange = (next: LogoEditorState, reason: "source" | "placement" | "enabled") => {
      logoState = next;
      logoDirty = true;
      guard?.markDirty();
      if (reason === "source") {
        refreshLogoEditorSection(panel, logoState, { business: record }, onLogoChange);
      }
    };
    wireLogoEditor(panel, logoState, { business: record }, onLogoChange);

    panel.querySelector("#logo-business")?.addEventListener("change", (e) => {
      const nextName = String((e.target as HTMLSelectElement).value || "").trim();
      const switchBiz = () => {
        selected = nextName;
        logoState = readLogoStateFromRecord(businesses[selected] || {});
        logoDirty = false;
        paint();
      };
      if (guard) guard.attemptLeave(switchBiz, { onSave: async () => { try { await saveHandler(); guard?.clear(); return true; } catch { return false; } } });
      else switchBiz();
    });

    wireFormScreen({
      panel,
      guard,
      errorEl: "#form-error",
      back: { button: "#back-btn" },
      save: {
        button: "#save-btn",
        onSave: saveHandler,
        afterSaveToast: "Logo saved.",
      },
    });
  };

  paint();
}

function renderHelp(panel: HTMLElement) {
  panel.innerHTML = `
    ${panelHeaderHtml({ title: "Help &amp; support" })}
    <p class="hint">Same resources as <a href="${SUPPORT_HUB_URL}" target="_blank" rel="noopener">frogswork.com/support</a>.</p>
    <p class="hint">To send quotes or price estimates, turn on <strong>Enable quotes</strong> under Settings → General. Set your invoice logo under Settings → General → Logo.</p>
    <a class="nav-card" href="${SUPPORT_ISSUES_URL}" target="_blank" rel="noopener">
      <span class="nav-card-title">Frequent issues</span>
      <span class="nav-card-hint">Install, sign-in, sync, and WebView2</span>
    </a>
    <a class="nav-card" href="${SUPPORT_GUIDES_URL}" target="_blank" rel="noopener">
      <span class="nav-card-title">Video guides</span>
      <span class="nav-card-hint">Short clips for setup and invoicing</span>
    </a>
    <a class="nav-card" href="${SUPPORT_CONTACT_URL}" target="_blank" rel="noopener">
      <span class="nav-card-title">Contact support</span>
      <span class="nav-card-hint">Email us if you are still stuck</span>
    </a>
    <a class="nav-card" href="${SUPPORT_HUB_URL}" target="_blank" rel="noopener">
      <span class="nav-card-title">Support hub</span>
      <span class="nav-card-hint">Open the full support page</span>
    </a>`;
  wireHubBackButton({ panel });
}

async function renderAccount(panel: HTMLElement, ctx: AppContext) {
  const acc = ctx.account;
  const lastSync = localStorage.getItem("frogswork_last_sync");
  const businesses = await cache.getBusinesses();
  const bizNames = Object.keys(businesses).sort();
  const fyOptions = listAuFinancialYearOptions();
  const defaultFy = fyOptions[0]?.token || "";

  const isDemo = Boolean(acc?.demo);
  const subLabel = isDemo
    ? "Demo (shared)"
    : acc?.active
      ? acc.canceling
        ? `Active (cancels ${acc.access_until ? new Date(acc.access_until).toLocaleDateString() : "at period end"})`
        : "Active"
      : "Inactive";
  const retentionHint = isDemo
    ? `<p class="hint">This is the shared feedback demo. Real email, billing, and account delete are disabled. Use Reset demo data to restore the sample workspace.</p>`
    : acc?.active
      ? acc.canceling
        ? `<p class="hint">You can keep using FrogsWork, including sending invoices, until the end of your billing period. After that your login stays, but your account data is kept for 30 days then deleted automatically unless you resubscribe.</p>`
        : `<p class="hint">Cancel, change plan, or update billing in Stripe. FrogsWork opens the secure Customer Portal. If you cancel, you keep full access until the period ends; your account data is kept for 30 days after that.</p>`
      : `<p class="hint">Your subscription is inactive. Resubscribe from the upgrade screen, or manage billing in Stripe.</p>`;

  panel.innerHTML = `
    <section class="panel">
      ${panelHeaderHtml({ title: "Account" })}
      <div class="preview-row"><span class="preview-label">Email</span><span>${esc(acc?.email || "")}</span></div>
      <div class="preview-row"><span class="preview-label">Subscription</span><span>${esc(subLabel)}</span></div>

      <h3 class="settings-subhead">${isDemo ? "Demo" : "Subscription"}</h3>
      ${retentionHint}
      ${
        isDemo
          ? `<button type="button" class="btn secondary" id="reset-demo">Reset demo data</button>`
          : acc?.portal_url
            ? `<a class="btn secondary" href="${esc(acc.portal_url)}" target="_blank" rel="noopener">Manage subscription</a>`
            : `<p class="hint">Subscription portal unavailable. Contact support if you need billing help.</p>`
      }
      ${
        !isDemo && acc && !acc.email_verified
          ? `<button type="button" class="btn small secondary" id="resend-verify" style="margin-top:0.5rem">Resend verification</button>`
          : ""
      }

      <h3 class="settings-subhead">Your data</h3>
      <p class="hint">Download a ZIP of your account data (JSON + invoice/quote PDFs) for your own records. FrogsWork cannot restore an export into your account later.</p>
      <div class="btn-row">
        <button type="button" class="btn secondary" id="export-data">Download my data</button>
      </div>

      <h3 class="settings-subhead">Tax time export</h3>
      <p class="hint">Income ledger CSV plus matching tax invoice PDFs for your accountant (Australian financial year). Separate from the full backup above.</p>
      <div class="field"><label>Financial year</label>
        <select id="tax-fy">
          ${fyOptions
            .map(
              (fy) =>
                `<option value="${esc(fy.token)}" ${fy.token === defaultFy ? "selected" : ""}>${esc(fy.display)}</option>`
            )
            .join("")}
        </select>
      </div>
      ${
        bizNames.length > 1
          ? `<div class="field"><label>Business</label>
              <select id="tax-business">
                <option value="">All businesses</option>
                ${bizNames.map((n) => `<option value="${esc(n)}">${esc(n)}</option>`).join("")}
              </select>
            </div>`
          : ""
      }
      <div class="btn-row">
        <button type="button" class="btn secondary" id="tax-export">Download tax pack</button>
      </div>

      ${
        isDemo
          ? ""
          : `<p class="hint" style="margin-top:0.75rem">Delete all businesses, customers, invoices, and PDFs from your FrogsWork account. Your login and subscription stay. This cannot be undone and cannot be restored.</p>
      <button type="button" class="btn danger" id="wipe-data">Delete all my data</button>

      <h3 class="settings-subhead">Close account</h3>
      <p class="hint">Cancels your Stripe subscription immediately and permanently erases your FrogsWork account and data. This cannot be undone and cannot be restored.</p>
      <button type="button" class="btn danger" id="delete-account">Delete account</button>`
      }

      <div class="nav-card card-static" style="margin-top:1.25rem">
        <span class="nav-card-title">Sync</span>
        <span class="nav-card-hint">${lastSync ? `Last sync: ${new Date(lastSync).toLocaleString()}` : "Not synced yet"}</span>
        <button type="button" class="btn small secondary" id="sync-now">Sync now</button>
      </div>
      <div class="btn-row" style="margin-top:1rem">
        <button type="button" class="btn secondary" id="sign-out">Sign out</button>
      </div>
    </section>`;

  wireHubBackButton({ panel });
  panel.querySelector("#sync-now")?.addEventListener("click", async () => {
    try {
      await pullBootstrap();
      await flushQueue(ctx.onSyncStatus);
      showToast("Synced.", "success");
      await renderAccount(panel, ctx);
    } catch (ex) {
      showToast(ex instanceof Error ? ex.message : "Sync failed.", "error");
    }
  });
  panel.querySelector("#sign-out")?.addEventListener("click", () => {
    clearSession();
    location.reload();
  });
  panel.querySelector("#resend-verify")?.addEventListener("click", async () => {
    try {
      await resendVerification();
      showToast("Verification email sent.", "success");
    } catch (ex) {
      showToast(ex instanceof Error ? ex.message : "Could not send.", "error");
    }
  });

  panel.querySelector("#export-data")?.addEventListener("click", async () => {
    const btn = panel.querySelector("#export-data") as HTMLButtonElement;
    btn.disabled = true;
    try {
      const blob = await downloadAccountExport();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `frogswork-export-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("Export downloaded. Keep it somewhere safe.", "success");
    } catch (ex) {
      showToast(ex instanceof Error ? ex.message : "Export failed.", "error");
    } finally {
      btn.disabled = false;
    }
  });

  panel.querySelector("#tax-export")?.addEventListener("click", async () => {
    const btn = panel.querySelector("#tax-export") as HTMLButtonElement;
    const fyEl = panel.querySelector("#tax-fy") as HTMLSelectElement | null;
    const bizEl = panel.querySelector("#tax-business") as HTMLSelectElement | null;
    const fy = String(fyEl?.value || "").trim();
    if (!fy) {
      showToast("Choose a financial year.", "error");
      return;
    }
    btn.disabled = true;
    try {
      const blob = await downloadTaxExport({
        fy,
        business: String(bizEl?.value || "").trim() || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `frogswork-tax-export-${fy}-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("Tax pack downloaded.", "success");
    } catch (ex) {
      showToast(ex instanceof Error ? ex.message : "Tax export failed.", "error");
    } finally {
      btn.disabled = false;
    }
  });

  panel.querySelector("#wipe-data")?.addEventListener("click", () => {
    void confirmWipeData(panel, ctx);
  });
  panel.querySelector("#delete-account")?.addEventListener("click", () => {
    void confirmDeleteAccount();
  });
  panel.querySelector("#reset-demo")?.addEventListener("click", async () => {
    const btn = panel.querySelector("#reset-demo") as HTMLButtonElement;
    btn.disabled = true;
    try {
      await resetDemoData();
      await cache.clearAll();
      await pullBootstrap();
      showToast("Demo data reset.", "success");
      await renderAccount(panel, ctx);
    } catch (ex) {
      showToast(ex instanceof Error ? ex.message : "Could not reset demo data.", "error");
    } finally {
      btn.disabled = false;
    }
  });
}

async function confirmWipeData(panel: HTMLElement, ctx: AppContext) {
  const confirmed = await openTypedConfirm({
    title: "Delete all my data",
    bodyHtml: `
      <p class="hint">Removes businesses, customers, invoices, and PDFs from your FrogsWork account. Your login and subscription stay.</p>
      <p class="hint"><strong>This cannot be undone and cannot be restored to FrogsWork.</strong></p>`,
    confirmPhrase: "DELETE DATA",
    confirmLabel: "Delete data",
    inputId: "wipe-confirm-input",
  });
  if (!confirmed) return;
  try {
    await deleteAccountData();
    await cache.clearAll();
    await pullBootstrap();
    showToast("All account data deleted.", "success");
    await renderAccount(panel, ctx);
  } catch (ex) {
    showToast(ex instanceof Error ? ex.message : "Could not delete data.", "error");
  }
}

async function confirmDeleteAccount() {
  const confirmed = await openTypedConfirm({
    title: "Delete account",
    bodyHtml: `
      <p class="hint">This cancels your Stripe subscription immediately and permanently erases your FrogsWork account and data.</p>
      <p class="hint"><strong>This cannot be undone and cannot be restored.</strong></p>
      <div class="field" style="margin-top:0.75rem">
        <label for="delete-account-password">Password</label>
        <input id="delete-account-password" type="password" autocomplete="current-password" />
      </div>`,
    confirmPhrase: "DELETE ACCOUNT",
    confirmLabel: "Delete account",
    inputId: "delete-account-confirm-input",
    requirePasswordId: "delete-account-password",
  });
  if (!confirmed || confirmed === true) return;
  try {
    await deleteAccount(confirmed.password);
  } catch (ex) {
    // The purge may have completed server-side even if the response failed.
    // If the account still exists, surface the error; otherwise fall through to sign-out.
    const stillExists = await fetchAccount().then(
      () => true,
      () => false
    );
    if (stillExists) {
      showToast(ex instanceof Error ? ex.message : "Could not delete account.", "error");
      return;
    }
  }
  forceSignOut();
}

function forceSignOut() {
  clearSession();
  void cache.clearAll().catch(() => {});
  location.replace("/");
}

async function openTypedConfirm(opts: {
  title: string;
  bodyHtml: string;
  confirmPhrase: string;
  confirmLabel: string;
  inputId: string;
  requirePasswordId?: string;
}): Promise<false | true | { password: string }> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "sheet-overlay";
    overlay.innerHTML = `
      <div class="sheet" role="dialog" aria-modal="true" aria-label="${esc(opts.title)}">
        <div class="sheet-handle" aria-hidden="true"></div>
        <h3 class="sheet-title">${esc(opts.title)}</h3>
        <div class="sheet-body">
          ${opts.bodyHtml}
          <div class="field" style="margin-top:0.75rem">
            <label for="${opts.inputId}">Type ${esc(opts.confirmPhrase)} to confirm</label>
            <input id="${opts.inputId}" type="text" autocomplete="off" />
          </div>
        </div>
        <div class="sheet-actions">
          <button type="button" class="btn secondary" data-action="cancel">Cancel</button>
          <button type="button" class="btn danger" data-action="confirm">${esc(opts.confirmLabel)}</button>
        </div>
      </div>`;
    const finish = (value: false | true | { password: string }) => {
      document.body.classList.remove("sheet-open");
      overlay.remove();
      resolve(value);
    };
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) finish(false);
    });
    overlay.querySelector('[data-action="cancel"]')?.addEventListener("click", () => finish(false));
    overlay.querySelector('[data-action="confirm"]')?.addEventListener("click", () => {
      const phraseEl = overlay.querySelector(`#${opts.inputId}`) as HTMLInputElement | null;
      if ((phraseEl?.value || "").trim() !== opts.confirmPhrase) {
        showToast(`Type ${opts.confirmPhrase} exactly to confirm.`, "error");
        return;
      }
      if (opts.requirePasswordId) {
        const pwEl = overlay.querySelector(`#${opts.requirePasswordId}`) as HTMLInputElement | null;
        const password = pwEl?.value || "";
        if (!password) {
          showToast("Password is required.", "error");
          return;
        }
        finish({ password });
        return;
      }
      finish(true);
    });
    document.body.classList.add("sheet-open");
    document.body.appendChild(overlay);
  });
}

async function renderEmailSending(panel: HTMLElement, ctx: AppContext) {
  const [settings, businesses] = await Promise.all([
    cache.getSettings(),
    cache.getBusinesses(),
  ]);
  const selfCopy = emailSelfCopyMode(settings);
  const defaultName = String(settings.default_business || Object.keys(businesses)[0] || "");
  const defaultBiz = (defaultName && businesses[defaultName]) || Object.values(businesses)[0] || {};
  const businessEmail = String(defaultBiz.email || "").trim();
  const accountEmail = String(ctx.account?.email || "").trim();
  const copyTarget = businessEmail || accountEmail || "your business email";
  const copyHint = businessEmail
    ? `Optionally send a copy to your business email (${esc(copyTarget)}). Change it under Business details.`
    : `Optionally send a copy to your business email. Set it under Business details (defaults to your account email${accountEmail ? `: ${esc(accountEmail)}` : ""}).`;

  panel.innerHTML = `
    <form id="email-send-form" class="panel" novalidate>
      ${panelHeaderHtml({ title: "Email sending" })}
      <p class="hint">When you tap Send, the customer gets the PDF. ${copyHint}</p>
      <p class="hint">Customer replies go to your business email (${esc(copyTarget)}), not FrogsWork.</p>
      <div class="field">
        <label for="email_self_copy">Copy to me</label>
        <select name="email_self_copy" id="email_self_copy">
          <option value="off" ${selfCopy === "off" ? "selected" : ""}>Don't copy me</option>
          <option value="cc" ${selfCopy === "cc" ? "selected" : ""}>CC (customer sees my email)</option>
          <option value="bcc" ${selfCopy === "bcc" ? "selected" : ""}>BCC (hidden from customer)</option>
        </select>
      </div>
      <p class="hint">Invoice and quote emails include your business name and details when set. They end with a short FrogsWork note linking to frogswork.com.</p>
      ${formActionsHtml({ saveId: "save-btn" })}
    </form>`;

  const form = panel.querySelector("#email-send-form") as HTMLFormElement;
  const guard = attachFormGuard(form);
  setGlobalSaveHandler(async () => {
    try {
      const fd = new FormData(form);
      const mode = String(fd.get("email_self_copy") || "cc");
      if (!["off", "cc", "bcc"].includes(mode)) throw new Error("Invalid copy option.");
      await upsertSettings({ email_self_copy: mode });
      await flushQueue(ctx.onSyncStatus);
      return true;
    } catch {
      return false;
    }
  });

  wireFormScreen({
    panel,
    guard,
    back: { button: "#back-btn" },
    save: {
      button: "#save-btn",
      onSave: async () => {
        const fd = new FormData(form);
        const mode = String(fd.get("email_self_copy") || "cc");
        if (!["off", "cc", "bcc"].includes(mode)) throw new Error("Invalid copy option.");
        await upsertSettings({ email_self_copy: mode });
        await flushQueue(ctx.onSyncStatus);
      },
      afterSaveToast: "Email preferences saved.",
    },
  });
}

function emailSelfCopyMode(settings: Record<string, unknown>): "off" | "cc" | "bcc" {
  const raw = String(settings.email_self_copy || "").trim().toLowerCase();
  if (raw === "off" || raw === "none") return "off";
  if (raw === "bcc") return "bcc";
  if (raw === "cc") return "cc";
  const ccSelf = !(
    settings.email_cc_self === false ||
    settings.email_cc_self === 0 ||
    settings.email_cc_self === "0"
  );
  const bccSelf =
    settings.email_bcc_self === true ||
    settings.email_bcc_self === 1 ||
    settings.email_bcc_self === "1" ||
    settings.email_bcc_self === "true";
  if (bccSelf && !ccSelf) return "bcc";
  if (!ccSelf && !bccSelf) return "off";
  return "cc";
}

async function renderPaymentTerms(panel: HTMLElement, ctx: AppContext) {
  const settings = await cache.getSettings();
  const prefs = dueRuleFromSettings(settings);
  const followupsOn =
    settings.payment_followups_enabled === true ||
    settings.payment_followups_enabled === 1 ||
    settings.payment_followups_enabled === "1" ||
    settings.payment_followups_enabled === "true";
  let offsetRaw = parseInt(String(settings.payment_followup_offset_days ?? "-3"), 10);
  if (!Number.isFinite(offsetRaw)) offsetRaw = -3;
  offsetRaw = Math.max(-14, Math.min(14, offsetRaw));
  panel.innerHTML = `
    <form id="terms-form" class="panel" novalidate>
      ${panelHeaderHtml({ title: "Default payment terms" })}
      <p class="hint">Used when creating new invoices.</p>
      ${dueRuleFieldsHtml(prefs, { showFixed: false })}
      <h2 style="margin-top:1.5rem">Payment follow-ups</h2>
      <p class="hint">Optional reminder emails for sent unpaid invoices. Off by default.</p>
      <label class="checkbox">
        <input type="checkbox" name="payment_followups_enabled" id="payment_followups_enabled" ${
          followupsOn ? "checked" : ""
        }>
        <span>Automatic payment follow-ups</span>
      </label>
      <div class="field">
        <label for="payment_followup_offset_days">Send follow-up (days relative to due date)</label>
        <input type="number" name="payment_followup_offset_days" id="payment_followup_offset_days"
          min="-14" max="14" step="1" value="${offsetRaw}">
        <p class="hint">Negative = before due (default −3). Positive = after due. Range −14 to 14. If already paid, the email asks the customer to ignore it.</p>
      </div>
      <p class="error-text" id="form-error" hidden></p>
      ${formActionsHtml({ saveId: "save-btn" })}
    </form>`;
  wireDueRuleToggles(panel);
  const form = panel.querySelector("#terms-form") as HTMLFormElement;
  const guard = attachFormGuard(form);
  setGlobalSaveHandler(async () => {
    try {
      const fd = new FormData(form);
      const due = dueRuleFromFormData(Object.fromEntries(fd.entries()), settings);
      let offset = parseInt(String(fd.get("payment_followup_offset_days") ?? "-3"), 10);
      if (!Number.isFinite(offset)) offset = -3;
      offset = Math.max(-14, Math.min(14, offset));
      await upsertSettings({
        due_rule_type: due.due_rule_type,
        due_net_days: due.due_net_days,
        payment_followups_enabled: Boolean(fd.get("payment_followups_enabled")),
        payment_followup_offset_days: offset,
      });
      await flushQueue(ctx.onSyncStatus);
      return true;
    } catch {
      return false;
    }
  });
  wireFormScreen({
    panel,
    guard,
    errorEl: "#form-error",
    back: { button: "#back-btn" },
    save: {
      button: "#save-btn",
      onSave: async () => {
        const fd = new FormData(form);
        const due = dueRuleFromFormData(Object.fromEntries(fd.entries()), settings);
        let offset = parseInt(String(fd.get("payment_followup_offset_days") ?? "-3"), 10);
        if (!Number.isFinite(offset)) offset = -3;
        offset = Math.max(-14, Math.min(14, offset));
        await upsertSettings({
          due_rule_type: due.due_rule_type,
          due_net_days: due.due_net_days,
          payment_followups_enabled: Boolean(fd.get("payment_followups_enabled")),
          payment_followup_offset_days: offset,
        });
        await flushQueue(ctx.onSyncStatus);
      },
      afterSaveToast: "Payment terms saved.",
    },
  });
}

async function renderBusiness(panel: HTMLElement, ctx: AppContext) {
  if (router.sub === "business-add" || router.sub === "business-edit") {
    return renderBusinessForm(panel, ctx);
  }
  const businesses = await cache.getBusinesses();
  const names = Object.keys(businesses);
  if (names.length <= 1) {
    const name = names[0] || "";
    return renderBusinessForm(panel, ctx, name, name ? businesses[name] : {});
  }
  panel.innerHTML = `
    ${panelHeaderHtml({
      title: "Business profiles",
      headerEndHtml: `<button type="button" class="btn small primary" id="add-business">Add</button>`,
    })}
    ${names.map((n) => businessCard(n, businesses[n])).join("")}`;
  panel.querySelector("#add-business")?.addEventListener("click", () =>
    router.navigate("settings", "business-add")
  );
  wireHubBackButton({ panel });
  panel.querySelectorAll("[data-edit-business]").forEach((btn) => {
    btn.addEventListener("click", () =>
      router.navigate("settings", "business-edit", {
        name: (btn as HTMLElement).dataset.editBusiness || "",
      })
    );
  });
}

function businessCard(name: string, record: Record<string, unknown>) {
  const display = String(record.business_name || name);
  const addr = formatAddressMultiline(record);
  return `<article class="card card-click" data-edit-business="${esc(name)}">
    <strong>${esc(display)}</strong>
    <div class="meta">${esc(addr.split("\n")[0] || "No address")}</div>
  </article>`;
}

async function renderBusinessForm(
  panel: HTMLElement,
  ctx: AppContext,
  forcedName: string | null = null,
  forcedRecord: Record<string, unknown> | null = null
) {
  const businesses = await cache.getBusinesses();
  const settings = await cache.getSettings();
  const editingName = forcedName ?? (router.params.name || "");
  const isAdd =
    router.sub === "business-add" || !editingName || !businesses[editingName];
  const record =
    forcedRecord ?? (editingName ? businesses[editingName] || {} : {});
  const accountEmail = String(ctx.account?.email || "").trim();
  const emailValue = String(record.email || "").trim() || accountEmail;
  let guard: ReturnType<typeof attachFormGuard> | null = null;
  const bizCount = Object.keys(businesses).length;
  const resolverContext = { businessCount: bizCount, isBusinessAdd: isAdd };

  const paintForm = () => {
    panel.innerHTML = `
    <form id="business-form" class="panel" novalidate>
      ${panelHeaderHtml({ title: isAdd ? "Add business" : "Business details" })}
      <p class="hint">Required fields are marked with *. This email is used when you CC/BCC yourself on invoice emails. Set your invoice logo under Settings → General → Logo.</p>
      ${abnLookupFieldHtml({
        name: "business_abn",
        id: "business_abn",
        value: record.business_abn || record.abn || "",
        label: "Business ABN",
        required: Boolean(record.gst_registered),
        optionalMark: true,
      })}
      <div class="field"><label>Business name *</label>
        <input name="business_name" id="business_name" value="${esc(record.business_name || editingName)}" required autocomplete="organization"></div>
      <div class="field"><label>Email *</label>
        <input name="email" type="email" value="${esc(emailValue)}" required autocomplete="email">
        <p class="hint">Defaults to your account email. Change it if invoices should copy a different address.</p></div>
      <div class="field"><label>Phone (optional)</label>
        <input name="phone" value="${esc(record.phone || "")}" type="tel" autocomplete="tel"></div>
      ${businessAddressFieldsHtml(record)}
      ${gstFieldsHtml(record)}
      ${bankFieldsHtml(record)}
      <p class="error-text" id="form-error" hidden></p>
      ${formActionsHtml({ saveId: "save-btn" })}
    </form>`;

    const form = panel.querySelector("#business-form") as HTMLFormElement;
    guard = attachFormGuard(form);

    const gstSelect = form.querySelector("#gst_registered") as HTMLSelectElement | null;
    const syncAbnRequired = () => {
      const yes = parseGstRegisteredForm(gstSelect?.value);
      form.querySelectorAll("[data-abn-req]").forEach((el) =>
        (el as HTMLElement).toggleAttribute("hidden", !yes)
      );
      form.querySelectorAll("[data-abn-opt]").forEach((el) =>
        (el as HTMLElement).toggleAttribute("hidden", yes)
      );
    };
    gstSelect?.addEventListener("change", syncAbnRequired);
    syncAbnRequired();

    const abnLookup = wireAbnLookup({
      root: form,
      abnInput: form.querySelector("#business_abn") as HTMLInputElement,
      nameInput: form.querySelector("#business_name") as HTMLInputElement,
      stateSelect: form.querySelector('select[name="state"]') as HTMLSelectElement | null,
      postcodeInput: form.querySelector('input[name="postcode"]') as HTMLInputElement | null,
      gstSelect,
    });

    const runSave = async () => {
      const fd = new FormData(form);
      const businessName = String(fd.get("business_name") || "").trim();
      if (!businessName) throw new Error("Business name is required.");
      const email = String(fd.get("email") || "").trim();
      if (!email || !email.includes("@")) throw new Error("A valid email is required.");
      const addr = normalizeAuAddress(readAddressFromForm(fd));
      if (!addr.address_line1) throw new Error("Street or PO Box is required.");
      if (!addr.suburb) throw new Error("Suburb is required.");
      if (!addr.state) throw new Error("State is required.");
      if (!addr.postcode) throw new Error("Postcode is required.");
      const accountName = String(fd.get("account_name") || "").trim();
      if (!accountName) throw new Error("Account name is required.");
      const bsbRaw = String(fd.get("bsb") || "").trim();
      const accRaw = String(fd.get("acc") || "").trim();
      if (!bsbRaw) throw new Error("BSB is required.");
      if (!accRaw) throw new Error("Account number is required.");
      const businessAbn = fd.get("business_abn")
        ? normalizeAbn(String(fd.get("business_abn")))
        : "";
      const gstRegistered = parseGstRegisteredForm(fd.get("gst_registered"));
      const abnOk = await confirmAbnBeforeSave({
        abnRaw: businessAbn,
        form: {
          name: businessName,
          state: addr.state,
          postcode: addr.postcode,
          gst_registered: gstRegistered,
        },
        handle: abnLookup,
      });
      if (!abnOk) throw new Error("__abn_cancelled");
      const profile: Record<string, unknown> = {
        business_name: businessName,
        email,
        phone: String(fd.get("phone") || "").trim(),
        ...addr,
        business_abn: businessAbn,
        gst_registered: gstRegistered,
        bsb: normalizeBsb(bsbRaw),
        acc: normalizeAccountNumber(accRaw),
        account_name: accountName,
        invoice_counter: record.invoice_counter || 1,
        logo_source_b64: record.logo_source_b64 || record.logo_b64 || "",
        logo_placement_by_template: record.logo_placement_by_template,
        logo_placement: record.logo_placement,
        logo_enabled: record.logo_enabled !== false && Boolean(record.logo_source_b64 || record.logo_b64),
        pdf_template: record.pdf_template || "classic",
      };
      const gstErr = validateBusinessGstSettings(profile);
      if (gstErr) throw new Error(gstErr);
      const key = isAdd ? businessName : editingName || businessName;
      if (isAdd && businesses[businessName]) {
        throw new Error("A business with this name already exists.");
      }
      await upsertBusiness(key, profile);
      if (!settings.default_business) await upsertSettings({ default_business: key });
      void flushQueue(ctx.onSyncStatus).then((flush) => {
        if (!flush.ok) {
          showToast(flush.error || "Saved locally but sync failed. Will retry when online.", "error");
        }
      });
    };

    setGlobalSaveHandler(async () => {
      try {
        await runSave();
        return true;
      } catch (ex) {
        if (ex instanceof Error && ex.message === "__abn_cancelled") return false;
        return false;
      }
    });

    wireFormScreen({
      panel,
      guard,
      errorEl: "#form-error",
      resolverContext,
      back: { button: "#back-btn" },
      save: {
        button: "#save-btn",
        onSave: async () => {
          try {
            await runSave();
          } catch (ex) {
            if (ex instanceof Error && ex.message === "__abn_cancelled") {
              throw new Error("Save cancelled.");
            }
            throw ex;
          }
        },
        afterSaveToast: "Business saved.",
      },
    });
  };

  paintForm();
}
